"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { getPdfjs } from "@/lib/invoice/importers";
import { COUNTRIES, detectPlace, findCountry } from "@/lib/invoice/geo";
import { CURRENCIES, fmtMoney, num, round2 } from "@/lib/invoice/utils";
import { currencyOptions } from "./options";
import { Button, Combobox, Field, Input, Modal, MonthTag, cx } from "./ui";

const COUNTRY_OPTIONS = COUNTRIES.map(([code, name, , currency]) => ({ value: name, label: name, hint: [code, currency].filter(Boolean).join(" · ") }));
const NEW = "__new__";
// Country to suggest when the address alone doesn't say (only unambiguous currencies).
const CURRENCY_COUNTRY = { GBP: "United Kingdom", USD: "United States", INR: "India", AUD: "Australia", CAD: "Canada", NZD: "New Zealand", AED: "United Arab Emirates", SGD: "Singapore", ZAR: "South Africa", CHF: "Switzerland", JPY: "Japan" };

// What the lines on the file add up to (with the file's tax), to sanity-check the amount.
const linesTotal = (p) =>
  round2((p.items || []).reduce((s, l) => s + num(l.amount ?? num(l.qty) * num(l.rate)), 0) + num(p.cgst) + num(p.sgst) + num(p.igst) + num(p.vat) - num(p.discount) - num(p.credit));

// Editable summary of one read invoice, pre-filled from the file.
function initialForm(p, store, matchClient) {
  const match = matchClient(store.clients, p.billTo);
  const country = match?.country || p.billTo?.country || "";
  return {
    clientId: match ? match.id : p.billTo?.name ? NEW : "",
    clientName: p.billTo?.name || "",
    country: findCountry(country)?.[1] || country,
    number: String(p.number || p.guessNumber || "").trim(),
    currency: CURRENCIES[p.currency] ? p.currency : match?.currency || "",
    total: p.total ? String(p.total) : String(linesTotal(p) || ""),
    date: p.date || "",
    dueDate: p.dueDate || "",
    paid: !p.status || p.status === "paid",
    partlyPaid: p.status && p.status !== "paid" && num(p.paid) > 0,
  };
}

// Apply the confirmed form back onto the parsed invoice for the importer.
export function applyForm(p, f, store) {
  const existing = f.clientId && f.clientId !== NEW ? store.clients.find((c) => c.id === f.clientId) : null;
  return {
    ...p,
    clientId: existing?.id || "",
    billTo: { ...(p.billTo || {}), name: existing ? existing.name : f.clientName.trim(), country: f.country || p.billTo?.country || "" },
    countryConfirmed: f.country || "",
    number: f.number.trim(),
    guessNumber: "",
    currency: f.currency || p.currency,
    total: num(f.total) || p.total,
    date: f.date,
    dueDate: f.dueDate,
    status: f.paid ? "paid" : "sent",
    paid: f.paid ? num(f.total) : f.partlyPaid ? num(p.paid) : 0,
    confirmed: true,
  };
}

// Page images of the original PDF / photo, or the lines for a spreadsheet row.
export function OriginalPreview({ p }) {
  const box = useRef(null);
  const [state, setState] = useState("loading");
  const [imgUrl, setImgUrl] = useState("");

  useEffect(() => {
    let cancelled = false;
    const el = box.current;
    if (el) el.innerHTML = "";
    setImgUrl("");
    const file = p.file;
    if (!file || !/\.pdf$/i.test(file.name || "") && !/^image\//.test(file.type || "") && !/\.(png|jpe?g|webp)$/i.test(file.name || "")) return setState("none");
    if (/^image\//.test(file.type) || /\.(png|jpe?g|webp)$/i.test(file.name)) {
      const u = URL.createObjectURL(file);
      setImgUrl(u);
      setState("ok");
      return () => URL.revokeObjectURL(u);
    }
    setState("loading");
    (async () => {
      try {
        const pdfjs = await getPdfjs();
        const doc = await pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()), isEvalSupported: false }).promise;
        for (let i = 1; i <= Math.min(doc.numPages, 6) && !cancelled; i++) {
          const page = await doc.getPage(i);
          const vp = page.getViewport({ scale: 1.6 });
          const c = document.createElement("canvas");
          c.width = vp.width;
          c.height = vp.height;
          c.className = "mb-3 w-full rounded-md bg-white shadow-lift ring-1 ring-slate-900/5";
          await page.render({ canvasContext: c.getContext("2d"), viewport: vp }).promise;
          if (!cancelled && el) el.appendChild(c);
        }
        if (!cancelled) setState("ok");
      } catch {
        if (!cancelled) setState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [p]);

  return (
    <div className="paper-solid h-full overflow-y-auto rounded-xl bg-slate-100 p-3" data-testid="confirm-preview">
      {state === "loading" && <div className="py-24 text-center text-sm text-slate-500">Showing the original…</div>}
      {state === "error" && <div className="py-24 text-center text-sm text-slate-500">Couldn’t show a preview of this file.</div>}
      {imgUrl && <img src={imgUrl} alt={p.source} className="w-full rounded-md bg-white shadow-lift" />}
      <div ref={box} />
      {state === "none" && (
        <div className="rounded-md bg-white p-4 text-xs shadow-lift">
          <div className="mb-2 font-semibold text-slate-700">{p.source}</div>
          {!(p.items || []).length && <div className="py-10 text-center text-slate-400">Spreadsheet – the lines appear here once read</div>}
          <table className="w-full">
            <thead className="text-left text-slate-500">
              <tr>
                <th className="py-1">Description</th>
                <th className="py-1 text-right">Qty</th>
                <th className="py-1 text-right">Rate</th>
              </tr>
            </thead>
            <tbody>
              {(p.items || []).map((l, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-1">{l.description}</td>
                  <td className="py-1 text-right">{l.qty}</td>
                  <td className="py-1 text-right">{num(l.rate).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// "Confirm invoice details" – one read invoice at a time: the original on the
// left, the details we found on the right (all editable). Nothing is imported
// until the last one is confirmed.
export function ImportConfirm({ drafts, store, matchClient, onReplace, onCancel, onDone }) {
  const [list, setList] = useState(() => drafts.map((p) => ({ p, f: initialForm(p, store, matchClient), skip: false })));
  const [i, setI] = useState(0);
  const [replacing, setReplacing] = useState("");
  const fileRef = useRef(null);
  const cur = list[i];
  const f = cur?.f;

  // no country on the invoice? work it out from the address (e.g. "Harrow" → United Kingdom)
  useEffect(() => {
    let off = false;
    list.forEach((x, k) => {
      if (x.f.country || !x.p.billTo) return;
      detectPlace(x.p.billTo)
        .catch(() => null)
        .then((place) => {
          const byCurrency = CURRENCY_COUNTRY[x.f.currency];
          const country = place?.country || (byCurrency && COUNTRIES.some((c) => c[1] === byCurrency) ? byCurrency : "");
          if (off || !country) return;
          setList((l) => l.map((y, j) => (j === k && !y.f.country ? { ...y, f: { ...y.f, country, countryGuess: !place?.country } } : y)));
        });
    });
    return () => {
      off = true;
    };
  }, [list.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const clientOptions = useMemo(
    () => [
      ...(f?.clientName && !store.clients.some((c) => c.name.toLowerCase() === f.clientName.toLowerCase()) ? [{ value: NEW, label: `${f.clientName}`, hint: "new client" }] : []),
      ...[...store.clients].sort((a, b) => a.name.localeCompare(b.name)).map((c) => ({ value: c.id, label: c.name, hint: [c.country, c.currency].filter(Boolean).join(" · ") })),
    ],
    [store.clients, f?.clientName]
  );

  if (!cur) return null;
  const set = (patch) => setList((l) => l.map((x, k) => (k === i ? { ...x, f: { ...x.f, ...patch } } : x)));
  const existingClient = f.clientId && f.clientId !== NEW ? store.clients.find((c) => c.id === f.clientId) : null;
  const dupe = f.number && store.invoices.some((x) => x.number === f.number && (!existingClient || x.clientId === existingClient.id));
  const lt = linesTotal(cur.p);
  const amountOff = num(f.total) && lt && Math.abs(num(f.total) - lt) > 0.05;
  const todo = list.filter((x) => !x.skip).length;
  const last = i === list.length - 1;

  const finish = (l = list) => {
    const chosen = l.filter((x) => !x.skip).map((x) => applyForm(x.p, x.f, store));
    onDone(chosen, l.filter((x) => x.skip).map((x) => x.p.source));
  };
  const next = (patch = {}) => {
    const l = list.map((x, k) => (k === i ? { ...x, ...patch } : x));
    setList(l);
    if (last) finish(l);
    else setI(i + 1);
  };
  const replace = async (file) => {
    if (!file) return;
    setReplacing(`Reading ${file.name}…`);
    try {
      const got = await onReplace(file, setReplacing);
      if (!got.length) throw new Error("No invoice found in that file");
      setList((l) => [...l.slice(0, i), ...got.map((p) => ({ p, f: initialForm(p, store, matchClient), skip: false })), ...l.slice(i + 1)]);
    } catch (e) {
      setReplacing(`⚠ ${e.message}`);
      setTimeout(() => setReplacing(""), 3000);
      return;
    }
    setReplacing("");
  };

  return (
    <Modal
      open
      xwide
      onClose={onCancel}
      title={
        <span className="flex items-center gap-3">
          Confirm invoice details
          {list.length > 1 && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600" data-testid="confirm-step">
              {i + 1} of {list.length}
            </span>
          )}
        </span>
      }
      footer={
        <>
          {list.length > 1 && (
            <div className="mr-auto flex items-center gap-1">
              <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => setI(i - 1)}>
                ← Back
              </Button>
              {list.map((x, k) => (
                <button
                  key={k}
                  onClick={() => setI(k)}
                  title={x.p.source}
                  className={cx("h-2 w-2 rounded-full transition", k === i ? "w-5 bg-brand-600" : x.skip ? "bg-slate-300" : k < i ? "bg-emerald-500" : "bg-slate-300/70")}
                />
              ))}
            </div>
          )}
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.csv" className="hidden" onChange={(e) => replace(e.target.files?.[0]).finally(() => (e.target.value = ""))} />
          <Button variant="ghost" onClick={() => next({ skip: true })} data-testid="confirm-skip">
            Skip this one
          </Button>
          <Button variant="secondary" disabled={!!replacing} onClick={() => fileRef.current?.click()}>
            {replacing && !replacing.startsWith("⚠") ? replacing : "Upload different file"}
          </Button>
          {!last && list.length > 2 && (
            <Button variant="secondary" onClick={() => finish(list.map((x, k) => (k === i ? { ...x, skip: false } : x)))} data-testid="confirm-all">
              Confirm all {todo}
            </Button>
          )}
          <Button onClick={() => next({ skip: false })} disabled={!f.clientId && !f.clientName.trim()} data-testid="confirm-next">
            {last ? `Confirm${todo > 1 ? ` & import ${todo}` : ""}` : "Confirm → next"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_1fr]" data-testid="import-confirm">
        <div className="h-[34vh] lg:h-[62vh]">
          <OriginalPreview p={cur.p} />
        </div>
        <div className="space-y-4">
          <div className="truncate text-xs text-slate-500" title={cur.p.source}>
            {cur.p.source}
            {cur.skip && <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-600">skipped</span>}
          </div>
          <Field label="Client name">
            <Combobox
              value={f.clientId}
              onChange={(v) => {
                const c = store.clients.find((x) => x.id === v);
                if (c) set({ clientId: c.id, country: c.country || f.country, currency: f.currency || c.currency || "" });
                else if (v === NEW) set({ clientId: NEW });
                else set({ clientId: NEW, clientName: v });
              }}
              options={clientOptions}
              placeholder="Choose or type a client"
              testId="confirm-client"
            />
            {f.clientId === NEW && <span className="mt-1 block text-[11px] text-blue-700">A new client “{f.clientName}” will be created</span>}
            {existingClient && <span className="mt-1 block text-[11px] text-emerald-700">Matched to your existing client</span>}
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Client country">
              <Combobox
                value={f.country}
                onChange={(country) => {
                  const cc = COUNTRIES.find((c) => c[1] === country);
                  set({ country, countryGuess: false, ...(!f.currency && cc?.[3] && CURRENCIES[cc[3]] ? { currency: cc[3] } : {}) });
                }}
                options={COUNTRY_OPTIONS}
                placeholder="Country"
                testId="confirm-country"
              />
              {f.countryGuess && <span className="mt-1 block text-[11px] text-slate-500">Guessed from the currency – change if needed</span>}
            </Field>
            <Field label="Invoice number">
              <Input value={f.number} onChange={(e) => set({ number: e.target.value })} data-testid="confirm-number" />
              {dupe && <span className="mt-1 block text-[11px] text-amber-700">Already in your invoices – it will be skipped</span>}
            </Field>
            <Field label="Invoice currency">
              <Combobox value={f.currency} onChange={(currency) => set({ currency })} options={currencyOptions} strict placeholder="Currency" testId="confirm-currency" />
            </Field>
            <Field label="Invoice amount">
              <Input type="number" step="0.01" value={f.total} onChange={(e) => set({ total: e.target.value })} data-testid="confirm-amount" />
              {amountOff ? (
                <span className="mt-1 block text-[11px] text-amber-700">The lines add up to {fmtMoney(lt, f.currency || "GBP")} – check the amount</span>
              ) : (
                <span className="mt-1 block text-[11px] text-slate-500">{(cur.p.items || []).length} line(s) read from the file</span>
              )}
            </Field>
            <Field label="Invoice date">
              <Input type="date" value={f.date} onChange={(e) => set({ date: e.target.value })} data-testid="confirm-date" />
              {f.date && <MonthTag date={f.date} className="mt-1" />}
            </Field>
            <Field label="Due date">
              <Input type="date" value={f.dueDate} onChange={(e) => set({ dueDate: e.target.value })} />
            </Field>
          </div>
          <Field label="Payment">
            <div className="inline-flex overflow-hidden rounded-lg text-sm font-semibold ring-1 ring-slate-200" role="group" data-testid="confirm-paid">
              <button type="button" onClick={() => set({ paid: true })} className={cx("px-4 py-1.5 transition", f.paid ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-emerald-50")}>
                Paid
              </button>
              <button type="button" onClick={() => set({ paid: false })} className={cx("px-4 py-1.5 transition", !f.paid ? "bg-amber-500 text-white" : "bg-white text-slate-600 hover:bg-amber-50")}>
                Unpaid
              </button>
            </div>
          </Field>
          {(cur.p.warnings || []).length > 0 && (
            <div className="space-y-1 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 ring-1 ring-amber-200">
              {cur.p.warnings.map((w) => (
                <div key={w}>{w}</div>
              ))}
            </div>
          )}
          {replacing.startsWith("⚠") && <div className="text-xs text-red-700">{replacing.slice(2)}</div>}
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// "Please wait while we read the invoice" – shown while each file is read: the
// original on the left straight away, live steps on the right.
const STEPS = [
  ["upload", "Uploading invoice"],
  ["extract", "Extracting data from file"],
  ["match", "Matching client & projects"],
];

function StepIcon({ state }) {
  if (state === "done")
    return (
      <span className="step-pop flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500 text-white shadow-sm shadow-emerald-500/40">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M5 12.5l4.5 4.5L19 7.5" className="step-tick" />
        </svg>
      </span>
    );
  if (state === "active")
    return (
      <span className="relative flex h-7 w-7 items-center justify-center" aria-hidden>
        {[0, 1, 2, 3, 4, 5, 6, 7].map((k) => (
          <span
            key={k}
            className="step-spoke absolute h-2 w-[3px] rounded-full bg-emerald-500"
            style={{ transform: `rotate(${k * 45}deg) translateY(-9px)`, animationDelay: `${(k - 8) * 0.1}s` }}
          />
        ))}
      </span>
    );
  return <span className="flex h-7 w-7 items-center justify-center" aria-hidden><span className="h-2.5 w-2.5 rounded-full bg-slate-300" /></span>;
}

export function ImportReading({ reading }) {
  const at = STEPS.findIndex(([k]) => k === reading.stage);
  const pct = /(\d+)%/.exec(reading.detail || "")?.[1];
  return (
    <Modal open xwide onClose={reading.onCancel} title={<span className="flex items-center gap-3">Reading your invoice{reading.total > 1 && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{reading.index + 1} of {reading.total}</span>}</span>}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_1fr]" data-testid="import-reading">
        <div className="relative h-[34vh] overflow-hidden rounded-xl lg:h-[62vh]">
          <OriginalPreview p={reading.preview} />
          {/* scanning light over the page while it's being read */}
          {reading.stage === "extract" && <div className="scan-beam pointer-events-none absolute inset-x-3 top-0 h-24" aria-hidden />}
        </div>
        <div className="flex flex-col justify-center px-2 py-6 lg:px-8">
          <h3 className="animate-slide-up text-xl font-semibold tracking-tight text-slate-900">Please wait while we read the invoice</h3>
          <p className="mt-1 truncate text-xs text-slate-500">{reading.preview.source}</p>
          <ol key={reading.index} className="mt-6 space-y-5">
            {STEPS.map(([k, label], n) => {
              const state = n < at ? "done" : n === at ? "active" : "todo";
              return (
                <li key={k} className="flex animate-slide-up items-start gap-3" style={{ animationDelay: `${n * 90}ms`, animationFillMode: "both" }} data-step={k} data-state={state}>
                  <StepIcon state={state} />
                  <div className="min-w-0 pt-0.5">
                    <div className={cx("text-base font-semibold transition-colors", state === "todo" ? "text-slate-400" : "text-emerald-700")}>{label}</div>
                    {state === "active" && reading.detail && (
                      <div className="mt-1 text-xs text-slate-500">
                        {reading.detail.replace(/…?\s*\d+%$/, "…")}
                        {pct && (
                          <div className="mt-1.5 h-1.5 w-48 overflow-hidden rounded-full bg-slate-200">
                            <div className="h-full rounded-full bg-emerald-500 transition-[width] duration-300" style={{ width: `${pct}%` }} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </Modal>
  );
}
